import { useState } from 'react';
import { EssentialsListDetails, TeacherDetails } from './types';
import TeacherProfileProjectsBar from './TeacherProfileProjectsBar';
import TeacherProfilePreviousProjectsBar from './TeacherProfilePreviousProjectsBar';
import { LightBoxWrapper } from '../common/LightBoxWrapper';

interface TeacherProfileQuickDonateCardSectionProps {
    teacherDetails: TeacherDetails,
    essentialsListDetails: EssentialsListDetails,
}

const SUPPLY_CATEGORIES: { name: string, imgSrc: string }[] = [
    { name: 'Books', imgSrc: `${baseCacheURL}images/shopping/book-sets.png` },
    { name: 'Art supplies', imgSrc: `${baseCacheURL}images/shopping/paint.png` },
    { name: 'Technology', imgSrc: `${baseCacheURL}images/shopping/laptops.png` },
    { name: 'Classroom supplies', imgSrc: `${baseCacheURL}images/shopping/pencils.png` },
    { name: 'STEM supplies', imgSrc: `${baseCacheURL}images/shopping/stem-kits.png` },
    { name: 'Music supplies', imgSrc: `${baseCacheURL}images/shopping/music-supplies.png` },
];

const SupplyCategoriesSection = () => (
    <div className="teacher-profile-supply-categories">
        <div className="supply-categories-card">
            <h4>Commonly ordered supplies on DonorsChoose</h4>
            <ul className="supply-categories-grid">
                {SUPPLY_CATEGORIES.map((cat) => (
                    <li key={cat.name} className="supply-category-item">
                        <div className="supply-category-image">
                            <img alt="" src={cat.imgSrc}/>
                        </div>
                        <span className="supply-category-name">{cat.name}</span>
                    </li>
                ))}
            </ul>
        </div>
    </div>
);

const TeacherProfileQuickDonateCardSection = (
    { teacherDetails, essentialsListDetails }: TeacherProfileQuickDonateCardSectionProps,
) => {
    const [isPreviousProjectsLightBoxOpen, setIsPreviousProjectsLightBoxOpen] = useState(false);
    const [shouldClosePreviousProjectsLightBox, setShouldClosePreviousProjectsLightBox] = useState(false);
    const hasFundedProjects = teacherDetails.numFundedProjects > 0;
    const hasActiveProjects = teacherDetails.numActiveProjects > 0;
    const hasEssentialsList = essentialsListDetails != null;
    const showSupplyCategoriesFallback = !hasActiveProjects && !hasEssentialsList;

    const renderEssentialsListTopItem = () => {
        return (essentialsListDetails &&
                   <a className="essentials-list-card mini" href={essentialsListDetails.essentialsListUrl}>
                       <div className="essentials-description">
                           <div className="list-details">
                               <h3 className="header">
                                   Classroom basics
                               </h3>
                               <p className="subtext">
                                   <span className="supply-name">{essentialsListDetails.essentialsTopItemSummary}</span>
                                   {essentialsListDetails.essentialsListLength > 0 && (
                                       <>
                                           {' and '}
                                           {essentialsListDetails.essentialsListLength} other
                                           {essentialsListDetails.essentialsListLength > 1 ? ' supplies' : ' supply'}.
                                       </>
                                   )}
                               </p>
                           </div>
                           <div className="top-supply" style={{ backgroundImage: `url(${essentialsListDetails.essentialsTopItemImageUrl})` }}>
                           </div>
                       </div>
                   </a>
        );
    };

    const hasCurrentRequests = !!(essentialsListDetails || hasActiveProjects);

    return (<div className="teacher-profile-requests-section">
        <div className="container">
            <h4>How funds are used</h4>
            <h2 className="thick">Where your generosity actually lands</h2>
            <p className="subheader">There&apos;s no shopping cart here — just a classroom and the things kids need to thrive in it. Every dollar goes directly toward the moments that make school feel exciting, safe, and full of possibility.</p>
            <div className="teacher-requests-container">
                {hasCurrentRequests && <p><b>Current requests</b></p>}
                <div className="">
                    {renderEssentialsListTopItem()}
                    {hasActiveProjects && <TeacherProfileProjectsBar teacherDetails={teacherDetails}/>}
                    {showSupplyCategoriesFallback && (
                        <SupplyCategoriesSection/>
                    )}
                </div>
            </div>
            {hasFundedProjects && (
                <button
                    type="button"
                    className="text-link previous-projects-link"
                    onClick={() => {
                        setShouldClosePreviousProjectsLightBox(false);
                        setIsPreviousProjectsLightBoxOpen(true);
                    }}>
                    View past projects
                </button>
            )}
        </div>
        {isPreviousProjectsLightBoxOpen && (
            <LightBoxWrapper
                additionalClasses="requests-side-drawer"
                closeLightbox={shouldClosePreviousProjectsLightBox}
                unMountHandler={() => {
                    setShouldClosePreviousProjectsLightBox(true);
                    setIsPreviousProjectsLightBoxOpen(false);
                }}>
                <div className="teacher-profile-past-requests-lightbox">
                    <h2>{teacherDetails.possessiveName} fully funded projects</h2>
                    <TeacherProfilePreviousProjectsBar teacherDetails={teacherDetails}/>
                </div>
            </LightBoxWrapper>
        )}
    </div>
    );
};

export default TeacherProfileQuickDonateCardSection;
